import { Send, Star } from "lucide-react";
import { useEffect, useState } from "react";
import {
  useDataProvider,
  useGetList,
  useGetMany,
  useNotify,
  useRefresh,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { EmptyState } from "../data-display/EmptyState";
import { SectionCard } from "../data-display/SectionCard";
import { StatusBadge, type StatusTone } from "../data-display/StatusBadge";
import { ListPageHeader } from "../list/ListPageHeader";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

const inputClass =
  "h-9 w-full rounded-md border border-input bg-transparent px-2.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

interface ReviewRequest {
  id: string;
  contact_id: number;
  job_id: string | null;
  channel: string;
  status: string;
  review_url: string | null;
  created_at: string;
}

interface ContactChoice {
  id: number;
  first_name: string;
  last_name: string;
}

const STATUS_TONES: Record<string, StatusTone> = {
  requested: "info",
  suppressed: "warning",
  completed: "success",
};

/**
 * Review requests: every ask goes through the consent-enforcing
 * compose_message gate server-side; refusals show up as "Suppressed",
 * never silently dropped. Jobs auto-ask on completion when enabled below.
 */
export const ReviewsPage = () => {
  const refresh = useRefresh();
  const { data: requests, isPending } = useGetList<ReviewRequest>(
    "review_requests",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "created_at", order: "DESC" },
    },
  );

  const { data: contacts } = useGetList<ContactChoice>("contacts_summary", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "first_name", order: "ASC" },
  });

  const contactName = (id: number) => {
    const contact = contacts?.find((c) => c.id === id);
    return contact ? `${contact.first_name} ${contact.last_name}` : `#${id}`;
  };

  if (isPending) return null;

  return (
    <>
      <ListPageHeader
        title="Reviews"
        subtitle="Ask happy customers for reviews, consent-safe, automatic after completed jobs"
        actions={
          <RequestReviewInline contacts={contacts ?? []} onDone={refresh} />
        }
      />

      <div className="grid gap-4 [&>*]:min-w-0 lg:grid-cols-[1fr_320px]">
        <SectionCard
          title="Requests"
          subtitle="Suppressed = the contact's consent settings blocked the ask"
        >
          {(requests ?? []).length === 0 ? (
            <div className="py-8">
              <EmptyState
                icon={Star}
                title="No review requests yet"
                description="Send your first ask, or complete a job with auto-requests enabled."
              />
            </div>
          ) : (
            <div className="flex flex-col">
              {(requests ?? []).map((request) => (
                <div
                  key={request.id}
                  className="flex items-center justify-between gap-3 border-b border-border-subtle py-2.5 text-[13px] last:border-0"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">
                      {contactName(request.contact_id)}
                    </p>
                    <p className="text-[12px] text-muted-foreground">
                      {new Date(request.created_at).toLocaleString()} ·{" "}
                      {request.channel.toUpperCase()}
                      {request.job_id ? " · auto (job completed)" : ""}
                    </p>
                  </div>
                  <StatusBadge
                    label={
                      request.status.charAt(0).toUpperCase() +
                      request.status.slice(1)
                    }
                    tone={STATUS_TONES[request.status] ?? "neutral"}
                  />
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <ReviewSettingsCard />
      </div>
    </>
  );
};

const RequestReviewInline = ({
  contacts,
  onDone,
}: {
  contacts: ContactChoice[];
  onDone: () => void;
}) => {
  const notify = useNotify();
  const [contactId, setContactId] = useState("");
  const [channel, setChannel] = useState("sms");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!contactId) {
      notify("Pick a contact first", { type: "warning" });
      return;
    }
    setSending(true);
    const { data, error } = await getSupabaseClient().rpc("request_review", {
      p_contact_id: Number(contactId),
      p_channel: channel,
    });
    setSending(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    const status = (data as { status: string }).status;
    notify(
      status === "suppressed"
        ? "Not sent, this contact's consent settings block review asks"
        : "Review request created",
      { type: status === "suppressed" ? "warning" : "info" },
    );
    setContactId("");
    onDone();
  };

  return (
    <div className="flex items-center gap-2">
      <select
        className={cn(inputClass, "w-52")}
        value={contactId}
        onChange={(e) => setContactId(e.target.value)}
      >
        <option value="">Choose a contact…</option>
        {contacts.map((contact) => (
          <option key={contact.id} value={contact.id}>
            {contact.first_name} {contact.last_name}
          </option>
        ))}
      </select>
      <select
        className={cn(inputClass, "w-24")}
        value={channel}
        onChange={(e) => setChannel(e.target.value)}
      >
        <option value="sms">SMS</option>
        <option value="email">Email</option>
      </select>
      <Button size="sm" disabled={sending} onClick={send}>
        <Send className="mr-1 size-3.5" />
        Ask for review
      </Button>
    </div>
  );
};

/** Workspace review settings (review link + auto-ask toggle). */
const ReviewSettingsCard = () => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const workspaceId = getActiveWorkspaceId();
  const { data: workspaces } = useGetMany("workspaces", {
    ids: workspaceId ? [workspaceId] : [],
  });
  const workspace = workspaces?.[0] as
    | { id: string; settings: Record<string, unknown> }
    | undefined;

  const [reviewUrl, setReviewUrl] = useState("");
  const [autoAsk, setAutoAsk] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!workspace) return;
    setReviewUrl((workspace.settings?.review_url as string) ?? "");
    setAutoAsk(Boolean(workspace.settings?.auto_review_requests));
  }, [workspace]);

  const save = async () => {
    if (!workspace) return;
    setSaving(true);
    try {
      await dataProvider.update("workspaces", {
        id: workspace.id,
        data: {
          settings: {
            ...workspace.settings,
            review_url: reviewUrl.trim() || null,
            auto_review_requests: autoAsk,
          },
        },
        previousData: workspace,
      });
      notify("Review settings saved");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Could not save", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="h-fit">
      <CardContent className="flex flex-col gap-3 py-4">
        <h2 className="text-[14px] font-semibold">Review settings</h2>
        <label className="flex flex-col gap-1 text-[12.5px] font-medium">
          Review link (Google, Yelp…)
          <input
            className={inputClass}
            placeholder="https://g.page/r/…/review"
            value={reviewUrl}
            onChange={(e) => setReviewUrl(e.target.value)}
          />
        </label>
        <label className="flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            className="size-4 accent-primary"
            checked={autoAsk}
            onChange={(e) => setAutoAsk(e.target.checked)}
          />
          Auto-ask when a job is completed
        </label>
        <p className="text-[12px] text-muted-foreground">
          Asks respect each contact's consent: opted-out or do-not-contact
          customers are never messaged.
        </p>
        <div className="flex justify-end">
          <Button size="sm" disabled={saving} onClick={save}>
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
